import React, { FC, memo, useState } from 'react';
import { useRequest } from 'ahooks';
import { usePageParams } from '../../../../hooks';
import { Button, Form, message, Space, Spin } from 'udesk-ui';
import styled from 'styled-components';
import OperatorList from './components/OperatorsList';
import Rules from 'src/components/common/Rules';
import { getProductCallRules, postProductCallRules } from 'src/api/productCallRules';
import { ProductCallRulesCreateRequest } from 'src/api/types';
import { InitialValues } from './const';
import UdeskLocales from 'UdeskLocales';

const Wrapper = styled.div``;

interface TemplateProps {
    match?: any;
    inspectDataSource: number;
}

interface FormModel extends Pick<ProductCallRulesCreateRequest, 'conditionList' | 'ruleList'> {}

const Template: FC<TemplateProps> = memo((props) => {
    const { inspectDataSource, ...restProps } = props;

    const { id, isReady } = usePageParams(restProps);

    const [form] = Form.useForm<FormModel>();

    const [initialValues, setInitialValues] = useState<FormModel>(InitialValues);

    const { run: flushGet, loading: getLoading } = useRequest(
        () =>
            getProductCallRules({
                params: {
                    mainProductId: id!,
                    inspectDataSource,
                },
            }),
        {
            ready: isReady,
            onSuccess: (res) => {
                const _initialValues = {
                    conditionList:
                        res.data?.conditionList?.map((c) => {
                            return {
                                ...c,
                                operatorList: c.taskOperatorList,
                            };
                        }) ?? InitialValues.conditionList,
                    ruleList: res.data?.ruleList ?? InitialValues.ruleList,
                };

                setInitialValues(_initialValues);
                form.setFieldsValue(_initialValues);
            },
        }
    );

    const [postLoading, setPostLoading] = useState(false);

    const onFinish = (values: FormModel) => {
        if (isReady) {
            setPostLoading(true);

            form.validateFields().then(
                () => {
                    postProductCallRules({
                        ...values,
                        mainProductId: id,
                        inspectDataSource,
                        relateConditionList: values.conditionList?.map((c) => ({
                            idCode: c.idCode,
                        })),
                    })
                        .then(() => {
                            message.success(
                                /* 操作成功 */ UdeskLocales['current'].pages.conversationAnalysis
                                    .productManage.components.acousticData.components.template.index
                                    .operationSuccessful
                            );
                            flushGet();
                        })
                        .finally(() => {
                            setPostLoading(false);
                        });
                },
                () => {
                    setPostLoading(false);
                }
            );
        }
    };

    return (
        <Wrapper>
            <Spin spinning={getLoading || postLoading}>
                <Form<FormModel> form={form} initialValues={initialValues} onFinish={onFinish}>
                    <Form.Item
                        label={
                            /* 算子 */ UdeskLocales['current'].pages.conversationAnalysis
                                .productManage.components.acousticData.components.template.index
                                .operator
                        }
                        name="conditionList"
                    >
                        <OperatorList {...restProps} />
                    </Form.Item>

                    <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, nextValues) =>
                            prevValues.conditionList !== nextValues.conditionList
                        }
                    >
                        {({ getFieldsValue }) => (
                            <Form.Item
                                label={
                                    /* 规则 */ UdeskLocales['current'].pages.conversationAnalysis
                                        .productManage.components.acousticData.components.template
                                        .index.rules
                                }
                                name="ruleList"
                            >
                                <Rules operatorList={getFieldsValue().conditionList} />
                            </Form.Item>
                        )}
                    </Form.Item>

                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit">
                                {/* 保存 */}
                                {
                                    UdeskLocales['current'].pages.conversationAnalysis.productManage
                                        .components.acousticData.components.template.index.preserve
                                }
                            </Button>
                            <Button htmlType="reset">
                                {/* 取消 */}
                                {
                                    UdeskLocales['current'].pages.conversationAnalysis.productManage
                                        .components.acousticData.components.template.index.cancel
                                }
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Spin>
        </Wrapper>
    );
});

export default Template;
